import Spacer from "@/components/Spacer";
import Button from "@/components/Button";
import { Box, Dialog as MuiDialog, IconButton } from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useCourses } from "@/pages/Courses/useCourses";
import { useStages } from "@/context/StagesContext";
import CardWithShadow from "@/components/CardWithShadow";
import { useGetCompletedCourses } from "@/hooks/useGetCompletedCourses";
import Text from "@/components/Text";
import Theme from "@/utils/theme";
import GraduationIcon from "@/ui/icons/GraduationIcon";
import SectionDescription from "@/components/SectionDescription";
import CourseCard from "./components/CourseCard";
import { useUser } from "@/context/UserContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useCallback, useState } from "react";
import {
  PDC_EMBED_CHROME_MESSAGE_TYPE,
  PDC_EMBED_CLOSE_MESSAGE_TYPE,
} from "@/utils/pdcEmbed";
import SadFaceIcon from "/assets/icons/sad-face.svg";
import Dialog from "@/components/Dialog";
import { ENABLE_POINTS_UI } from "@/utils/feature-flags";
import { isPdcExternalCourseListEnabled } from "@/utils/pdc-external-course-list";
import { CompleteRegistrationModal } from "@/components/CompleteRegistrationModal";

function Courses() {
  const usePdcCourseList = isPdcExternalCourseListEnabled();
  const { isValidStage, currentStageNumber, isUserOutOfReality } = useStages();
  const { completedCoursesCount, totalCoursesCount, isLoading } = useGetCompletedCourses({
    disableQuery: usePdcCourseList,
  });
  const {
    courses,
    onGoToCourse,
    courseEmbedOpen,
    onCloseCourseEmbed,
    pdcEmbedSrc,
    pdcListStats,
  } = useCourses(usePdcCourseList ? true : !isLoading);

  const handleCloseCourseEmbed = useCallback(() => {
    onCloseCourseEmbed();
  }, [onCloseCourseEmbed]);

  /** PDC iframe asks to hide parent close on exam route (only PDC X + abandon flow). */
  const [showPdcParentCloseButton, setShowPdcParentCloseButton] = useState(true);

  /** Reset only when modal closes — do not force true on open (race with iframe + React Strict Mode). */
  useEffect(() => {
    if (!courseEmbedOpen) {
      setShowPdcParentCloseButton(true);
    }
  }, [courseEmbedOpen]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === PDC_EMBED_CLOSE_MESSAGE_TYPE) {
        handleCloseCourseEmbed();
        return;
      }
      if (
        event.data?.type === PDC_EMBED_CHROME_MESSAGE_TYPE &&
        typeof event.data?.showParentCloseButton === "boolean"
      ) {
        setShowPdcParentCloseButton(event.data.showParentCloseButton);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleCloseCourseEmbed]);

  const displayFinishedCoursesCount =
    usePdcCourseList && pdcListStats
      ? pdcListStats.completedCoursesCount
      : completedCoursesCount;
  const displayTotalCourses =
    usePdcCourseList && pdcListStats
      ? pdcListStats.totalCoursesCount
      : totalCoursesCount > 0
        ? totalCoursesCount
        : courses?.length ?? 0;
  const { userInfo } = useUser();
  const navigate = useNavigate();

  if (!userInfo) return null;

  if (isUserOutOfReality === true)
    return (
      <Dialog
        isOpen={true}
        icon={<img src={SadFaceIcon} />}
        text="Infelizmente não foi dessa vez. Você não avançou para a próxima etapa. Mas não deixe de tentar na próxima edição."
        confirmButtonText="Voltar"
        onConfirm={async () => {
          navigate(-1);
        }}
      />
    );

  return (
    <div>
      <CompleteRegistrationModal />
      <MuiDialog
        fullScreen
        open={courseEmbedOpen}
        onClose={handleCloseCourseEmbed}
        PaperProps={{
          sx: {
            margin: 0,
            maxHeight: "none",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            flex: 1,
            width: "100%",
            minHeight: 0,
            height: "100dvh",
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${Theme.colors.neutral.light}`,
              bgcolor: Theme.colors.background.secondary,
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            <Button
              type="button"
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={handleCloseCourseEmbed}
              aria-label="Voltar à listagem de cursos"
            >
              Voltar aos cursos
            </Button>
            {showPdcParentCloseButton ? (
              <IconButton
                type="button"
                aria-label="Fechar plataforma de cursos"
                onClick={handleCloseCourseEmbed}
                sx={{ color: Theme.colors.neutral.mid_dark }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </Box>
          {/* Percent height on iframe needs a parent with explicit height; minHeight alone is not enough */}
          <Box
            component="iframe"
            title="Plataforma de cursos"
            src={pdcEmbedSrc}
            sx={{
              flex: 1,
              width: "100%",
              minHeight: 0,
              border: 0,
              display: "block",
            }}
          />
        </Box>
      </MuiDialog>

      <Box mb="1rem">
        <Button
          type="button"
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/dashboard")}
          aria-label="Voltar para o portal (página inicial)"
        >
          Voltar para o portal
        </Button>
      </Box>

      <SectionDescription
        title="Complete os cursos"
        description={
          ENABLE_POINTS_UI
            ? "Mestre, através do botão {{'Iniciar curso'}} você {{acessará}} os cursos para conquistar mais {{pontos}}. Fique atento: os cursos devem {{ser assistidos}} até o final e o exame {{final}} deve ser realizado para {{aumentar}} a pontuação."
            : "Mestre, através do botão {{'Iniciar curso'}} você {{acessará}} os conteúdos. Fique atento: os cursos devem {{ser assistidos}} até o final e o exame {{final}} deve ser realizado para avançar na jornada."
        }
      />

      <CardWithShadow px="1.5rem" py="1rem">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb="1rem"
        >
          <Text>Cursos Disponíveis</Text>

          <Box
            display="flex"
            p="0.5rem"
            border={`1px solid ${Theme.colors.neutral.light}`}
            borderRadius="0.5rem"
            alignItems="center"
          >
            <GraduationIcon size={20} />
            <Text color={Theme.colors.neutral.mid_dark} ml="0.3rem">
              Cursos finalizados:{" "}
            </Text>
            <Text>
              {displayFinishedCoursesCount}/{displayTotalCourses}
            </Text>
          </Box>
        </Box>

        {courses?.map((item) => (
          <Box key={item.pdcCourseId ?? item.title}>
            <CourseCard
              imageSrc={item.thumbnailUrl}
              title={item.title}
              lockButton={!item.courseUrl}
              courseEnded={!isValidStage || currentStageNumber > 1}
              onClick={() => onGoToCourse(item.courseUrl)}
              course={item}
            />

            <Spacer height="2rem" />
          </Box>
        ))}
      </CardWithShadow>
    </div>
  );
}

export default Courses;
